import { TsDOM as q } from '@/scripts/core/TsDOM'

type RestockRocketSellingPlan = {
	shopify_selling_plan_id?: number
	shipping_text?: string
	variant_ids?: number[]
}

type SyncSellingPlanInputArgs = {
	form: HTMLFormElement
	variantId?: string | number | null
	preorder: boolean
	liquidSellingPlanId?: string | number | null
}

type SyncShippingTextInputArgs = {
	form: HTMLFormElement
	preorder: boolean
	shippingText?: string | null
}

type ApplyPreorderPropertiesArgs = {
	formData: FormData
	preorder: boolean
	shippingText?: string | null
}

const SELECTORS = {
	sellingPlanInput: '[data-uc-selling-plan-id]',
	shippingTextInput: '[data-uc-stoq-shipping-text]',
}

const PROPERTIES = {
	preorder: '_preorder',
	shippingText: '_stoq_shipping_text',
}

function normalizeSellingPlanId(value?: string | number | null): string {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? String(value) : ''
	}

	if (typeof value !== 'string') return ''

	const normalized = value.trim()
	return /^\d+$/.test(normalized) ? normalized : ''
}

function getRestockRocketSellingPlan(
	variantId?: string | number | null
): RestockRocketSellingPlan | null {
	if (variantId !== undefined && variantId !== null) {
		const numericVariantId = Number(variantId)
		if (Number.isFinite(numericVariantId)) {
			return window._RestockRocket?.getSellingPlan?.(numericVariantId) ?? null
		}
	}

	return window._RestockRocket?.getSellingPlan?.() ?? null
}

function normalizeOptionalText(value?: string | null): string {
	if (typeof value !== 'string') return ''
	return value.trim()
}

function getRestockRocketSellingPlanForVariant(
	variantId?: string | number | null
): RestockRocketSellingPlan | null {
	const numericVariantId = Number(variantId)
	if (!Number.isFinite(numericVariantId)) return null

	const sellingPlan = getRestockRocketSellingPlan(numericVariantId)
	if (!sellingPlan) return null

	const variantIds = Array.isArray(sellingPlan.variant_ids) ? sellingPlan.variant_ids : []
	if (variantIds.length > 0 && !variantIds.includes(numericVariantId)) return null

	return sellingPlan
}

function getRestockRocketSellingPlanIdForVariant(
	variantId?: string | number | null
): string {
	const sellingPlan = getRestockRocketSellingPlanForVariant(variantId)
	if (!sellingPlan) return ''

	return normalizeSellingPlanId(sellingPlan.shopify_selling_plan_id)
}

function getRestockRocketShippingTextForVariant(
	variantId?: string | number | null
): string {
	return normalizeOptionalText(
		getRestockRocketSellingPlanForVariant(variantId)?.shipping_text
	)
}

function setLineItemProperty(formData: FormData, propertyName: string, value: string) {
	const propertyKey = formData.get('items[0][id]')
		? `items[0][properties[${propertyName}]]`
		: propertyName

	if (value) {
		formData.set(propertyKey, value)
		return
	}

	formData.delete(propertyKey)
}

export function syncStoqSellingPlanInput({
	form,
	variantId,
	preorder,
	liquidSellingPlanId,
}: SyncSellingPlanInputArgs) {
	const sellingPlanInput = q.os<HTMLInputElement>(SELECTORS.sellingPlanInput, form)
	if (!sellingPlanInput) return
	const existingSellingPlanId = normalizeSellingPlanId(sellingPlanInput.value)

	if (!preorder) {
		sellingPlanInput.value = ''
		sellingPlanInput.disabled = true
		return
	}

	const sellingPlanId =
		normalizeSellingPlanId(liquidSellingPlanId) ||
		getRestockRocketSellingPlanIdForVariant(variantId) ||
		existingSellingPlanId

	sellingPlanInput.value = sellingPlanId
	sellingPlanInput.disabled = sellingPlanId === ''
}

export function syncStoqShippingTextInput({
	form,
	preorder,
	shippingText,
}: SyncShippingTextInputArgs) {
	const shippingTextInput = q.os<HTMLInputElement>(SELECTORS.shippingTextInput, form)
	if (!shippingTextInput) return

	shippingTextInput.value = preorder ? normalizeOptionalText(shippingText) : ''
}

export function getStoqShippingTextInputValue(form: HTMLFormElement): string {
	return normalizeOptionalText(
		q.os<HTMLInputElement>(SELECTORS.shippingTextInput, form)?.value
	)
}

export function renderStoqButtonForVariant(variantId?: string | number | null) {
	const normalizedVariantId = Number(variantId)
	if (!Number.isFinite(normalizedVariantId)) return

	window._RestockRocket?.renderButtonForVariant?.(normalizedVariantId)
}

export function applyStoqPreorderProperties({
	formData,
	preorder,
	shippingText,
}: ApplyPreorderPropertiesArgs) {
	setLineItemProperty(formData, PROPERTIES.preorder, preorder ? 'true' : '')
	setLineItemProperty(
		formData,
		PROPERTIES.shippingText,
		preorder
			? normalizeOptionalText(shippingText) ||
				getRestockRocketShippingTextForVariant(
					formData.get('id') || formData.get('items[0][id]')
				)
			: ''
	)
}
